import { Fragment, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PrinterIcon, FilePdfIcon, FileXlsIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";

export const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : (v ?? "—"));

const STATUS_CLS = {
  RECONCILED: "text-emerald-300 border-emerald-500/40", "IN PROGRESS": "text-sky-300 border-sky-500/40",
  EXCEPTION: "text-rose-300 border-rose-500/40", "INSUFFICIENT EVIDENCE": "text-amber-300 border-amber-500/40",
  ...Object.fromEntries(["VARIANCE", "SHORTAGE", "EXCESS", "OVER-CONSUMED", "NO WO RECEIPT", "MATERIAL DIFFERENCE", "CONSUMPTION DIFFERENCE",
    "PRODUCTION DIFFERENCE", "TRANSFER DIFFERENCE", "UNRESOLVED STAGE"].map(s => [s, "text-rose-300 border-rose-500/40"])),
  ...Object.fromEntries(["COMMON POOL", "ISSUED BALANCE", "RECEIPT NOT SEPARATELY RECORDED", "Not separately recorded", "CONTEXT — DEPARTMENT-WIDE"]
    .map(s => [s, "text-amber-200 border-amber-500/30"])),
};
export const Status = ({ s, tid }) => (
  <span data-testid={tid} className={`inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest whitespace-nowrap ${STATUS_CLS[s] || "text-zinc-300 border-zinc-600"}`}>{s || "—"}</span>
);

const TXN_COLS = [["posting_date", "Date"], ["transaction_type", "Type"], ["process_stage", "Stage"], ["item_code", "Item"], ["batch_lot", "Batch"],
  ["source_document_type", "Source"], ["source_document_no", "Document"], ["wo_no", "WO"], ["cwo_no", "CWO"], ["order_no", "SO / ESO"],
  ["po_no", "PO"], ["quantity", "Signed Qty"], ["uom", "UOM"]];

function TxnDialog({ drill, onClose }) {
  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent onEscapeKeyDown={() => onClose()} className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="wor-drill-dialog">
        <DialogHeader><DialogTitle className="font-display">{drill?.title}</DialogTitle></DialogHeader>
        {drill?.link && <a href={drill.link} data-testid="wor-drill-dept-link" className="text-xs text-[var(--accent)] inline-flex items-center gap-1 hover:underline">Open departmental reconciliation <ArrowSquareOutIcon size={12} /></a>}
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="wor-drill-table">
            <thead><tr className="border-b border-[var(--border)]">{TXN_COLS.map(([, l]) => <th key={l} className="px-2 py-1.5 text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500 whitespace-nowrap">{l}</th>)}</tr></thead>
            <tbody>{(drill?.txns || []).map(t => (
              <tr key={t.movement_id} className="border-b border-[var(--border)] font-mono text-xs">
                {TXN_COLS.map(([k]) => <td key={k} className={`px-2 py-1.5 whitespace-nowrap ${k === "quantity" ? "text-right tabular-nums" : ""} ${String(t[k] || "").startsWith("Not Linked") ? "text-zinc-500 italic" : ""}`}>{k === "quantity" ? fmt(t[k]) : (t[k] || "—")}</td>)}
              </tr>))}</tbody>
          </table>
          {!(drill?.txns || []).length && <div className="p-3 text-xs text-zinc-500">No Stock V2 transactions for this line.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Generic read-only section table; rows with `txns` open the shared drill panel.
export function ReconSection({ id, title, cols, rows, drillTitle, note }) {
  const [drill, setDrill] = useState(null);
  return (
    <section className="tactical-card p-4 space-y-2" data-testid={`wor-section-${id}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="font-display text-base md:text-lg">{title}</h2>
        {note && <span className="text-[11px] text-zinc-500">{note}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[var(--border)]">{cols.map(c => <th key={c.k} className={`px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 whitespace-nowrap ${c.num ? "text-right" : "text-left"}`}>{c.l}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <Fragment key={i}>
              <tr className="border-b border-[var(--border)] text-xs font-mono" data-testid={`wor-${id}-row-${i}`}>
                {cols.map(c => {
                  const v = r[c.k];
                  if (c.status) return <td key={c.k} className="px-2 py-1.5"><Status s={v} tid={`wor-${id}-status-${i}`} /></td>;
                  const scoped = r.txns_by?.[c.k] ?? r.txns;
                  const clickable = c.drill && scoped && typeof v === "number" && Math.abs(v) > 1e-9;
                  return (
                    <td key={c.k} className={`px-2 py-1.5 whitespace-nowrap ${c.num ? "text-right tabular-nums" : ""} ${String(v || "").startsWith("Not") ? "text-zinc-500 italic" : ""}`}>
                      {clickable
                        ? <button type="button" data-testid={`wor-${id}-drill-${c.k}-${i}`} className="text-[var(--accent)] hover:underline"
                            onClick={() => setDrill({ title: `${drillTitle(r)} · ${c.l}`, txns: scoped, link: r.link })}>{fmt(v)}</button>
                        : (c.num ? fmt(v) : (v ?? "—"))}
                    </td>);
                })}
              </tr>
            </Fragment>
          ))}</tbody>
        </table>
        {!rows.length && <div className="p-3 text-xs text-zinc-500" data-testid={`wor-${id}-empty`}>No rows for this scope.</div>}
      </div>
      <TxnDialog drill={drill} onClose={() => setDrill(null)} />
    </section>
  );
}

const errMsg = async (e) => {
  try { const d = JSON.parse((await e?.response?.data?.text?.()) || "{}").detail; return d?.message || d || "Export failed"; }
  catch { return "Export failed"; }
};

export function ReconExports({ params, disabled }) {
  const get = async (format) => {
    const res = await api.get("/reports/wo-consolidated-reconciliation/export", { params: { format, ...params }, responseType: "blob" });
    return { blob: res.data, name: /filename="([^"]+)"/.exec(res.headers?.["content-disposition"] || "")?.[1] || `wo-reconciliation.${format}` };
  };
  const download = ({ blob, name }) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(u), 5000); };
  const print = ({ blob }) => {
    const u = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
    document.getElementById("__wor_print")?.remove();
    const f = document.createElement("iframe"); f.id = "__wor_print"; f.style.cssText = "position:fixed;width:0;height:0;border:0;"; f.src = u;
    f.onload = () => { try { f.contentWindow.focus(); f.contentWindow.print(); } catch { window.open(u, "_blank"); } };
    document.body.appendChild(f);
  };
  const run = async (format, then) => { try { then(await get(format)); } catch (e) { toast.error(await errMsg(e)); } };
  const btn = "h-9 px-3 rounded-sm border border-[var(--border)] text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 hover:bg-[var(--bg-2)] disabled:opacity-40";
  return (
    <div className="flex gap-2">
      <button type="button" data-testid="wor-print" disabled={disabled} className={btn} onClick={() => run("pdf", print)}><PrinterIcon size={14} /> Print</button>
      <button type="button" data-testid="wor-pdf" disabled={disabled} className={btn} onClick={() => run("pdf", download)}><FilePdfIcon size={14} /> Download PDF</button>
      <button type="button" data-testid="wor-xlsx" disabled={disabled} className={btn} onClick={() => run("xlsx", download)}><FileXlsIcon size={14} /> Export Excel</button>
    </div>
  );
}
